//#region Imports
import React, { useState, useEffect } from "react";
import { StyleSheet, Platform, SafeAreaView, View, ScrollView, TouchableOpacity, useWindowDimensions } from "react-native";
import { useTheme } from '@react-navigation/native';
import CustomLabel from "../../../components/CustomLabel/CustomLabel";
import CustomButton from "../../../components/CustomButton";
import { createCommonStyles } from '../../../styles/commonStyles';
import { useTranslation } from "react-i18next";
import Ionicons from "@react-native-vector-icons/ionicons/static";
import CustomInput from "../../../components/CustomInput";
import { Checkbox } from 'react-native-paper';
import HRAdminDocFormScreen from "../../HR/HRAdminTrainings/HRAdminDocFormScreen";
import CustomDropDown from "../../../components/CustomDropDown/CustomDropdown";
import { formatDateForUI } from "../../../constants/FormatDate";
import CustomCalendar from "../../../components/CustomCalendar/CustomCalendar";
import { useTraining } from "../../../hooks/useTraining";
import Swal from 'sweetalert2';
import { generateUniqueID } from "../../../components/SurveyBuilderComponent/CreateSurvey/CreateSurveyFunctions";

const MyTrainingsDetailsScreen = ({ trnInfo, trnUI, isEdit, isUserEditable, setIsNew, isNew, ldNeed, trnProvider,
    ldMethod, loadUserTRN, loadTRNDetails, selectMyTrainingsScreen }) => {
    //#region Const Definitions
    const { width, height } = useWindowDimensions();
    const { t } = useTranslation();
    const { colors } = useTheme();
    const common = createCommonStyles(colors);
    const styles = makeStyles(colors, width, height);
    const { insertUserTrainingData, updateUserTrainingData, loadTrainingMaterial, DeleteTrainingMaterial, SaveTrainingMaterial } = useTraining();

    const [trnData, setTrnData] = useState([]);

    /* Calendar Related state variables */
    const [calendarVisible, setCalendarVisible] = useState(false);
    const [calendarItem, setCalendarItem] = useState(null);
    const [calendarSelectedDate, setCalendarSelectedDate] = useState(null);

    const openCalendar = (itemName, currentDate) => {
        setCalendarItem(itemName);
        setCalendarSelectedDate(currentDate || new Date().toISOString().split('T')[0]);
        setCalendarVisible(true);
    };

    const closeCalendar = () => {
        setCalendarVisible(false);
        setCalendarItem(null);
        setCalendarSelectedDate(null);
    };

    const onCalendarDateChange = (newDate) => {
        handleChange(calendarItem, newDate);
        closeCalendar();
    };
    //#endregion

    //#region UseEffect
    useEffect(() => {
        if (trnInfo.length > 0) {
            setTrnData(trnInfo[0]);
        }
        if (isNew)
            setTrnData({
                courseName: '',
                startDate: '',
                endDate: '',
                userEditable: '',
                trainingProvider: '',
                days: 0,
                hrs: 0,
                workhrs: 0,
                ldNeed: '',
                ldMethod: '',
                accepted: 0,
                doc: '',
            })
    }, [trnInfo]);

    // Calculate Duration (hrs)
    const calculateDuration = () => {
        return trnData.workhrs || 0;
    };

    // Handler for input changes
    const handleChange = (field, value) => {
        setTrnData(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    //#region Handle Save
    const handleSave = () => {
        if (isNew) {
            const newTrnData = {
                ...trnData,
                trnUI: generateUniqueID(),
            };
            insertUserTrainingData(newTrnData).then((res) => {
                if (res === 200) {
                    showMessage({
                        message: t("common:saveSuccess"),
                        type: "success",
                        duration: 3000,
                    });
                    setIsNew(false);
                    loadUserTRN();
                    selectMyTrainingsScreen();
                }
            }).catch((err) => {
                showMessage({
                    message: t('common:errorInsert'),
                    type: "danger",
                    duration: 10000,
                })
            })
        } else {
            if (trnData.accepted) {
                Swal.fire({
                    title: t('common:areusure'),
                    text: t('common:confirmCompletion'),
                    icon: "question",
                    showCancelButton: true,
                    reverseButtons: true,
                    cancelButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Cancel</text>`,
                    cancelButtonColor: '#d3d3d3',
                    confirmButtonText: `<text style="font-family:'Roboto-Medium', font-size:16px">Okay</text>`,
                    confirmButtonColor: '#A0A2FF',
                }).then((result) => {
                    if (result.isConfirmed) {
                        updateUserTrainingData(trnData).then((res) => {
                            loadTRNDetails(trnUI)
                            showMessage({
                                message: t("common:saveSuccess"),
                                type: "success",
                                duration: 3000,
                            });
                        }).catch((err) => {
                            showMessage({
                                message: t('common:errorUpdating'),
                                type: "danger",
                                duration: 10000,
                            })
                        });
                        return;
                    }
                    onCancel();
                });
            } else {
                updateUserTrainingData(trnData).then((res) => {
                    loadTRNDetails(trnUI)
                    showMessage({
                        message: t("common:saveSuccess"),
                        type: "success",
                        duration: 3000,
                    });
                }).catch((err) => {
                    showMessage({
                        message: t('common:errorUpdating'),
                        type: "danger",
                        duration: 10000,
                    })
                });
            }
        }
    }

    const onCancel = () => {
        if (isNew) {
            setTrnData({
                courseName: '',
                startDate: '',
                endDate: '',
                userEditable: '',
                trainingProvider: '',
                days: 0,
                hrs: 0,
                workhrs: 0,
                ldNeed: '',
                ldMethod: '',
                accepted: 0,
                doc: '',
            })
        } else {
            loadTRNDetails(trnUI)
        }
    }
    //#endregion

    const handleGoBack = () => {
        selectMyTrainingsScreen();
    }

    //#region Return
    return (
        <SafeAreaView style={styles.mainView}>
            <View style={[{ flexDirection: 'row', justifyContent: 'space-between', paddingHorizontal: 10 }]}>
                <TouchableOpacity onPress={() => { handleGoBack() }} style={styles.backButton}>
                    <Ionicons name="arrow-back" size={30} color="black" />
                    <CustomLabel
                        value={t('common:TRNDetails')}
                        type='MINORTITLE'
                    />
                </TouchableOpacity>
                {(isEdit || !isUserEditable) && !isNew && 
                    <CustomButton
                        text={trnData.accepted ? t('common:accepted') : t('common:acknowledgeCompletion')}
                        iconName={trnData.accepted ? "checkmark-done-outline" : "checkmark-outline"}
                        show={'BUTTON_ADD'}
                        type={'BUTTON_ADD'}
                        sz={20}
                        onPress={() => { handleChange('accepted', 1) }}
                    />}
                {/* Save / Cancel Buttons */}
                {(isEdit || !isUserEditable) && (
                    <View style={{ flexDirection: 'row', justifyContent: 'flex-end' }}>
                        <CustomButton
                            text={t('common:save')}
                            type="BUTTON_SAVE"
                            isGradient={true}
                            onPress={() => { handleSave() }}
                        />
                        <CustomButton
                            text={t('common:cancel')}
                            type="BUTTON_CANCEL"
                            onPress={() => { onCancel() }}
                        />
                    </View>
                )}
            </View>

            <ScrollView contentContainerStyle={{ padding: 20 }}>
                <View style={common.cardstyle}>
                    <View style={styles.twoColumnContainer}>
                        {/* Left Column */}
                        <View style={styles.column}>
                            {/* Course Name */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:courseName')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <CustomInput
                                            value={trnData.courseName}
                                            setValue={(text) => handleChange('courseName', text)}
                                            placeholder={t('common:courseName')}
                                            type="MAININPUT"
                                        />
                                    ) : (
                                        <CustomLabel value={trnData.courseName} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* Start Date */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:startDate')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                            <CustomInput
                                                value={formatDateForUI(trnData.startDate)}
                                                setValue={() => { }}
                                                maxLen={50}
                                                editable={false}
                                                type="MAININPUT"
                                            />
                                            <Ionicons
                                                name={calendarVisible ? "calendar" : "calendar-outline"}
                                                style={styles.icons}
                                                onPress={() => calendarVisible ? closeCalendar() : openCalendar('startDate', trnData.startDate)}
                                            />
                                        </View>
                                    ) : (
                                        <CustomLabel value={formatDateForUI(trnData.startDate)} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* End Date */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:endDate')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                                            <CustomInput
                                                value={formatDateForUI(trnData.endDate)}
                                                setValue={() => { }}
                                                maxLen={50}
                                                editable={false}
                                                type="MAININPUT"
                                            />
                                            <Ionicons
                                                name={calendarVisible ? "calendar" : "calendar-outline"}
                                                style={styles.icons}
                                                onPress={() => calendarVisible ? closeCalendar() : openCalendar('endDate', trnData.endDate)}
                                            />
                                        </View>
                                    ) : (
                                        <CustomLabel value={formatDateForUI(trnData.endDate)} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* User Editable */}
                            <View style={[styles.fieldRow, { alignItems: 'center' }]}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:userEditable')} type="SUBTITLE" />
                                </View>
                                <View style={[styles.inputContainer, { flexDirection: 'row', alignItems: 'center' }]}>
                                    <Checkbox
                                        status={trnData.userEditable ? 'checked' : 'unchecked'}
                                        onPress={() => { }}
                                    />
                                </View>
                            </View>
                        </View>

                        {/* Right Column */}
                        <View style={styles.column}>
                            {/* Training Provider */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:trainingProvider')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <CustomDropDown
                                            disabled={!isEdit}
                                            defValue={trnData.trainingProvider}
                                            options={trnProvider}
                                            value={trnData.trainingProvider}
                                            placeholder={t('common:trainingProvider')}
                                            setValue={e => handleChange('trainingProvider', e)}
                                            type="FULL"
                                        />
                                    ) : (
                                        <CustomLabel value={trnData.trainingProvider} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* Duration (hrs) - calculated, read-only */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:durationHrs')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <CustomInput
                                            value={calculateDuration().toString()}
                                            setValue={(text) => handleChange('hrs', text)}
                                            placeholder={t('common:durationHrs')}
                                            type="MAININPUT"
                                        />
                                    ) : (
                                        <CustomLabel value={calculateDuration().toString()} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* Learning & Development Need */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:ldNeed')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <CustomDropDown
                                            disabled={!isEdit}
                                            defValue={trnData.ldNeed}
                                            options={ldNeed}
                                            value={trnData.ldNeed}
                                            placeholder={t('common:ldNeed')}
                                            setValue={e => handleChange('ldNeed', e)}
                                            type="FULL"
                                        />
                                    ) : (
                                        <CustomLabel value={trnData.ldNeed} type="MAININPUT" />
                                    )}
                                </View>
                            </View>

                            {/* Learning & Development Method */}
                            <View style={styles.fieldRow}>
                                <View style={styles.labelContainer}>
                                    <CustomLabel value={t('common:ldMethod')} type="SUBTITLE" />
                                </View>
                                <View style={styles.inputContainer}>
                                    {isEdit ? (
                                        <CustomDropDown
                                            disabled={!isEdit}
                                            defValue={trnData.ldMethod}
                                            options={ldMethod}
                                            value={trnData.ldMethod}
                                            placeholder={t('common:ldMethod')}
                                            setValue={e => handleChange('ldMethod', e)}
                                            type="FULL"
                                        />
                                    ) : (
                                        <CustomLabel value={trnData.ldMethod} type="MAININPUT" />
                                    )}
                                </View>
                            </View>
                        </View>
                    </View>

                    <SafeAreaView style={{ alignItems: 'center' }}>
                        {calendarVisible && (
                            <CustomCalendar
                                calendarType={'RangePicker'}
                                setRange={(range) => onCalendarDateChange(range.startDate)}
                                range={{ startDate: calendarSelectedDate, endDate: calendarSelectedDate }}
                                allowSingleDate={true}
                                startingYear={1950}
                            />
                        )}</SafeAreaView>
                </View>
                <View style={common.cardstyle}>
                    {!isNew && <HRAdminDocFormScreen
                        isEdit={true}
                        isAccepted={trnData.accepted || !isEdit}
                        isUser={true}
                        trnUI={trnData.trainingnoUI}
                        employeeOptions={[]}
                        loadDocForm={loadTrainingMaterial}
                        saveDocForm={SaveTrainingMaterial}
                        DeleteDocForm={DeleteTrainingMaterial}
                    />}
                </View>
            </ScrollView>

        </SafeAreaView>
    );
}

const makeStyles = (colors, width, height) => StyleSheet.create({
    mainView: {
        flex: 1,
        height: Platform.OS === "web" ? (height * 60) / 100 : "95%",
        justifyContent: "flex-start",
    },
    twoColumnContainer: {
        flexDirection: 'row',
        justifyContent: 'space-between',
    },
    column: {
        flex: 1,
        paddingHorizontal: 10,
    },
    fieldRow: {
        flexDirection: 'row',
        alignItems: 'center',
        marginVertical: 10,
    },
    labelContainer: {
        width: '20%',
        paddingRight: 10,
    },
    inputContainer: {
        width: '80%',
    },
    backButton: {
        alignSelf: 'flex-start',
        padding: 10,
        flexDirection: 'row',
        margin: 0,
        alignItems: 'center',
    },
    icons: {
        fontSize: 25,
        color: colors.text_primary,
    },
});

export default MyTrainingsDetailsScreen;
